/**
 * FauxDS Toast
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Toast component for prototyping.
 * It is NOT the real @faire/slate Toast and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Toast, see: @faire/slate/components/toast
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { useEffect, useState } from "react";

export interface ToastProps {
  /**
   * The content/description of the toast
   */
  description: string;
  /**
   * Whether the toast is visible
   * @default false
   */
  open?: boolean;
  /**
   * The CTA button text (replaces close button when provided)
   */
  cta?: string;
  /**
   * Callback when CTA is clicked
   */
  onCtaClick?: () => void;
  /**
   * Callback when toast is dismissed
   */
  onDismiss?: () => void;
  /**
   * Auto-dismiss timeout in ms (4000 for regular, 6000 for CTA)
   * Set to false to disable auto-dismiss
   * @default 4000
   */
  timeout?: number | false;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

// Close icon SVG
const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4 4L12 12M12 4L4 12"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

// Info icon SVG
const InfoIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M10 9V14"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="10" cy="6.5" r="1" fill="currentColor" />
  </svg>
);

/**
 * Toast - FauxDS (recreated from Slate)
 *
 * Toasts contain information directly related to an action performed.
 * This is a simplified implementation for prototyping purposes.
 */
export default function Toast({
  description,
  open = false,
  cta,
  onCtaClick,
  onDismiss,
  timeout = 4000,
  className = "",
}: ToastProps) {
  const [isVisible, setIsVisible] = useState(open);
  const [isAnimating, setIsAnimating] = useState(false);

  // Handle open state changes
  useEffect(() => {
    if (open) {
      setIsVisible(true);
      // Small delay to trigger animation
      requestAnimationFrame(() => setIsAnimating(true));
    } else {
      setIsAnimating(false);
      // Wait for animation to complete before hiding
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // Auto-dismiss
  useEffect(() => {
    if (!open || timeout === false) return;

    const autoTimeout = cta ? 6000 : timeout;
    const timer = setTimeout(() => {
      onDismiss?.();
    }, autoTimeout);

    return () => clearTimeout(timer);
  }, [open, timeout, cta, onDismiss]);

  const handleClose = () => {
    onDismiss?.();
  };

  const handleCtaClick = () => {
    onCtaClick?.();
    onDismiss?.();
  };

  if (!isVisible) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        "fixed bottom-6 left-1/2 flex -translate-x-1/2 items-center gap-3 rounded-[var(--slate-radius-component-default)] bg-[var(--slate-color-surface-primary-inverse)] px-4 py-3 text-[var(--slate-color-text-primary-inverse)] shadow-lg transition-all duration-200 ease-out",
        isAnimating ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0",
        className
      )}
      style={{ zIndex: 9999 }}
    >
      {/* Icon */}
      <span className="shrink-0 text-[var(--slate-color-icon-primary-inverse)]">
        <InfoIcon />
      </span>

      {/* Description */}
      <span className="flex-1 font-sans text-[14px] leading-[20px]">
        {description}
      </span>

      {/* CTA or Close button */}
      {cta ? (
        <button
          onClick={handleCtaClick}
          className="shrink-0 font-sans text-[14px] leading-[20px] font-medium text-[var(--slate-color-text-primary-inverse)] underline transition-opacity hover:opacity-80"
        >
          {cta}
        </button>
      ) : (
        <button
          onClick={handleClose}
          aria-label="Dismiss"
          className="shrink-0 p-1 text-[var(--slate-color-icon-primary-inverse)] transition-opacity hover:opacity-80"
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
}
